/**
 * Goods Receipt API Client
 * Epic 21: Bevételezés (Goods Receipt Management)
 *
 * Endpoints:
 *   - POST /purchase-advice — Create avizo
 *   - GET  /purchase-advice/pending — Pending avizos
 *   - GET  /purchase-advice/:id — Get avizo by ID
 *   - GET  /purchase-advice/:id/items — Get avizo items
 *   - PATCH /purchase-advice/:id — Update avizo
 *   - POST /purchase-advice/:id/cancel — Cancel avizo
 *   - POST /receipts — Create receipt
 *   - GET  /receipts/:id — Get receipt by ID
 *   - GET  /receipts/:id/items — Get receipt items
 *   - POST /receipts/:id/complete — Complete receipt
 *   - POST /receipts/:receiptId/discrepancies — Report discrepancy
 *   - GET  /receipts/:receiptId/discrepancies — List discrepancies
 *   - POST /receipts/:receiptId/discrepancies/:discrepancyId/resolve — Resolve discrepancy
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export type ReceiptStatus = 'DRAFT' | 'IN_PROGRESS' | 'DISCREPANCY' | 'COMPLETED';
export type AvizoStatus = 'PENDING' | 'RECEIVED' | 'CANCELLED';
export type DiscrepancyType = 'SHORTAGE' | 'SURPLUS' | 'DAMAGED' | 'WRONG_ITEM';
export type PurchaseCategory = 'ARU' | 'BERUHAZAS' | 'BERENDEZES' | 'EGYEB' | 'REZSI';

export interface AvizoItem {
  id: string;
  purchaseAdviceId: string;
  productId: string;
  productCode: string;
  productName: string;
  expectedQuantity: number;
  unitPrice: number;
}

export interface Avizo {
  id: string;
  tenantId: string;
  supplierId: string;
  supplierName: string;
  expectedDate: string;
  status: AvizoStatus;
  notes?: string;
  pdfUrl?: string;
  items: AvizoItem[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReceiptItem {
  id: string;
  receiptId: string;
  avizoItemId?: string;
  productId: string;
  productCode: string;
  productName: string;
  expectedQuantity: number;
  receivedQuantity: number;
  unitPrice: number;
  locationCode?: string;
  purchaseCategory?: PurchaseCategory;
  packageQuantity?: number;
  unitsPerPackage?: number;
}

export interface Receipt {
  id: string;
  tenantId: string;
  receiptNumber: string;
  purchaseAdviceId?: string;
  supplierId: string;
  supplierName: string;
  status: ReceiptStatus;
  notes?: string;
  // Backend list endpoint (`GET /receipts`) omits nested items for
  // perf; items are served via `GET /receipts/:id/items`. Only the
  // detail endpoint populates this array.
  items?: ReceiptItem[];
  completedAt?: string;
  completedBy?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface Discrepancy {
  id: string;
  receiptId: string;
  receiptItemId: string;
  type: DiscrepancyType;
  expectedQuantity: number;
  actualQuantity: number;
  reason?: string;
  resolved: boolean;
  resolvedAt?: string;
  resolvedBy?: string;
  resolutionNote?: string;
  createdBy: string;
  createdAt: string;
}

// ============================================
// INPUT TYPES
// ============================================

export interface CreateAvizoInput {
  supplierId: string;
  supplierName: string;
  expectedDate: string;
  notes?: string;
  items: Array<{
    productId: string;
    productCode: string;
    productName: string;
    expectedQuantity: number;
    unitPrice: number;
  }>;
}

export interface CreateReceiptInput {
  purchaseAdviceId?: string;
  supplierId: string;
  supplierName: string;
  notes?: string;
  items: Array<{
    avizoItemId?: string;
    productId: string;
    productCode: string;
    productName: string;
    expectedQuantity: number;
    receivedQuantity: number;
    unitPrice: number;
    locationCode?: string;
    purchaseCategory?: PurchaseCategory;
    packageQuantity?: number;
    unitsPerPackage?: number;
  }>;
}

export interface CreateDiscrepancyInput {
  receiptItemId: string;
  type: DiscrepancyType;
  expectedQuantity: number;
  actualQuantity: number;
  reason?: string;
}

export interface ResolveDiscrepancyInput {
  resolutionNote: string;
  notifySupplier?: boolean;
}

export interface ReceiptFilters {
  status?: ReceiptStatus;
  search?: string;
}

// ============================================
// AVIZO (PURCHASE ADVICE) API CALLS
// ============================================

export async function createAvizo(input: CreateAvizoInput) {
  return api.post<{ data: Avizo }>('/purchase-advice', input);
}

export async function getPendingAvizos() {
  return api.get<{ data: Avizo[] }>('/purchase-advice/pending');
}

export async function getAvizoById(id: string) {
  return api.get<{ data: Avizo }>(`/purchase-advice/${id}`);
}

export async function getAvizoItems(id: string) {
  return api.get<{ data: AvizoItem[] }>(`/purchase-advice/${id}/items`);
}

export async function cancelAvizo(id: string) {
  return api.post<{ data: Avizo }>(`/purchase-advice/${id}/cancel`, {});
}

// ============================================
// RECEIPT API CALLS
// ============================================

export async function createReceipt(input: CreateReceiptInput) {
  return api.post<{ data: Receipt }>('/receipts', input);
}

export async function getReceiptById(id: string) {
  return api.get<{ data: Receipt }>(`/receipts/${id}`);
}

export async function getReceiptItems(id: string) {
  return api.get<{ data: ReceiptItem[] }>(`/receipts/${id}/items`);
}

export async function completeReceipt(id: string) {
  return api.post<{ data: Receipt }>(`/receipts/${id}/complete`, {});
}

// ============================================
// DISCREPANCY API CALLS
// ============================================

export async function createDiscrepancy(receiptId: string, input: CreateDiscrepancyInput) {
  return api.post<{ data: Discrepancy }>(`/receipts/${receiptId}/discrepancies`, input);
}

export async function getDiscrepancies(receiptId: string, unresolvedOnly?: boolean) {
  const params = unresolvedOnly ? '?unresolvedOnly=true' : '';
  return api.get<{ data: Discrepancy[] }>(`/receipts/${receiptId}/discrepancies${params}`);
}

export async function resolveDiscrepancy(
  receiptId: string,
  discrepancyId: string,
  input: ResolveDiscrepancyInput
) {
  return api.post<{ data: Discrepancy }>(
    `/receipts/${receiptId}/discrepancies/${discrepancyId}/resolve`,
    input
  );
}

// ============================================
// SALES-FG-4-3: SHIPPING COST DISTRIBUTION
// ============================================

export interface ShippingAllocationItem {
  receiptItemId: string;
  productCode: string;
  productName: string;
  originalUnitPrice: number;
  itemTotalValue: number;
  proportion: number;
  shippingAllocation: number;
  adjustedUnitPrice: number;
}

export interface ShippingDistributionResult {
  receiptId: string;
  shippingCost: number;
  receiptTotalValue: number;
  items: ShippingAllocationItem[];
}

export async function distributeShipping(receiptId: string, shippingCost: number) {
  return api.post<{ data: ShippingDistributionResult }>(
    `/receipts/${receiptId}/distribute-shipping`,
    { shippingCost }
  );
}
